import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Scale, Globe, FileCheck, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import StarBorder from "../../components/signO/ui/StarBorder";

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-dark text-white p-8 pt-24">
      <Link
        to="/"
        className="fixed top-8 left-8 text-gray-400 hover:text-white transition-colors z-50"
      >
        <ArrowLeft className="w-6 h-6" />
      </Link>

      <div className="max-w-4xl mx-auto space-y-16">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-600"
          >
            Legally Sound, Globally Recognized.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-400 max-w-2xl mx-auto"
          >
            Our platform adheres to the highest global standards for electronic
            signatures, ensuring that every contract executed is admissible and
            enforceable.
          </motion.p>
        </section>

        {/* Compliance */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400">
              <Globe className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold">Compliance Frameworks</h2>
            <p className="text-gray-400 text-lg">
              Our signatures meet the legal requirements of key global
              frameworks, providing non-repudiation through verified identity
              and immutable records.
            </p>
          </div>
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 p-4 rounded text-center font-mono text-sm">
                ESIGN Act (USA)
              </div>
              <div className="bg-white/5 p-4 rounded text-center font-mono text-sm">
                eIDAS (EU)
              </div>
              <div className="bg-white/5 p-4 rounded text-center font-mono text-sm">
                IT Act 2000 (India)
              </div>
              <div className="bg-white/5 p-4 rounded text-center font-mono text-sm">
                PIPEDA (Canada)
              </div>
            </div>
          </div>
        </section>

        {/* Evidence Package */}
        <section className="bg-white/5 rounded-2xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <FileCheck className="w-8 h-8 text-yellow-400" />
            <h2 className="text-3xl font-bold">The Evidence Package</h2>
          </div>
          <p className="text-gray-400 mb-6">
            Every finalized document is delivered with a comprehensive Evidence
            Package for court admissibility.
          </p>

          <ul className="grid md:grid-cols-2 gap-4">
            <li className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              Verifiable signature audit trail
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              Unique Blockchain hash and transaction ID
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              IP addresses and timestamps
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              Evidence of identity verification (KYC)
            </li>
          </ul>
        </section>

        {/* Non-Repudiation */}
        <section className="text-center max-w-3xl mx-auto space-y-4">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 mx-auto">
            <Lock className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-bold">Non-Repudiation Guaranteed</h2>
          <p className="text-gray-400 text-lg">
            Because identity is verified <em>before</em> signing and the
            document is immutable <em>after</em> signing, the risk of a party
            successfully denying their commitment is virtually eliminated.
          </p>
        </section>

        {/* CTA */}
        <div className="text-center pt-8">
          <Link to="/signup">
            <StarBorder
              as="button"
              color="cyan"
              speed="4s"
              className="text-lg px-8 py-4"
            >
              Start Securing My Documents
            </StarBorder>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
